import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react'
import { useImageUpload } from '@/hooks/use-image-upload'

interface ImageUploadProps {
  currentImage?: string
  onImageChange: (imageUrl: string) => void
  folder?: string
  label?: string
  description?: string
  circular?: boolean
}

export function ImageUpload({ 
  currentImage, 
  onImageChange, 
  folder = 'images',
  label = 'Image',
  description = 'Upload an image file',
  circular = false
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentImage || null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { uploadImage, uploading, error } = useImageUpload()

  // Update preview when currentImage changes
  useEffect(() => {
    setPreview(currentImage || null)
  }, [currentImage])

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Show preview immediately
    const reader = new FileReader()
    reader.onload = (e) => {
      setPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // Upload to Supabase
    const imageUrl = await uploadImage(file, folder)
    if (imageUrl) {
      onImageChange(imageUrl)
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    
    const file = e.dataTransfer.files[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleRemoveImage = () => {
    setPreview(null)
    onImageChange('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const openFileDialog = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="image-upload">{label}</Label>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card 
        className={`border-2 border-dashed transition-colors ${
          dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
        } ${circular ? 'max-w-fit' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
      >
        <CardContent className={circular ? "p-4" : "p-6"}>
          {preview ? (
            <div className={circular ? "space-y-2" : "space-y-4"}>
              <div className={`relative ${circular ? 'flex justify-center' : ''}`}>
                <img 
                  src={preview} 
                  alt="Preview" 
                  className={
                    circular 
                      ? "w-20 h-20 object-cover rounded-full border-2 border-border neo-avatar"
                      : "w-full h-48 object-cover rounded-lg border"
                  }
                  onError={(e) => {
                    // Hide broken image
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className={circular ? "absolute top-0 right-0 rounded-full h-6 w-6 p-0" : "absolute top-2 right-2"}
                  onClick={handleRemoveImage}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex gap-2 justify-center">
                <Button
                  type="button"
                  variant="outline"
                  size={circular ? "sm" : "default"}
                  onClick={openFileDialog}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {uploading ? 'Uploading...' : 'Change Image'}
                </Button>
              </div>
            </div>
          ) : (
            <div className={`text-center ${circular ? 'space-y-2 flex flex-col items-center' : 'space-y-4'}`}>
              <div className={`mx-auto ${circular ? 'w-20 h-20 rounded-full' : 'w-12 h-12 rounded-lg'} bg-muted flex items-center justify-center ${circular ? 'neo-avatar border-2 border-border' : ''}`}>
                <ImageIcon className={`${circular ? 'h-8 w-8' : 'h-6 w-6'} text-muted-foreground`} />
              </div>
              {!circular && (
                <div>
                  <p className="text-sm font-medium">Upload an image</p>
                  <p className="text-xs text-muted-foreground">
                    Drag and drop or click to select
                  </p>
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size={circular ? "sm" : "default"}
                onClick={openFileDialog}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {uploading ? 'Uploading...' : circular ? 'Upload' : 'Select Image'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  )
}